import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { TextField, InputAdornment, IconButton, CircularProgress } from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import QrCodeIcon from "@mui/icons-material/QrCode";
import { QRCodeSVG } from "qrcode.react";
import axios from "axios";
import { toast } from "react-toastify";

const API_BASE =
  import.meta.env.VITE_APP_BASE_API + import.meta.env.VITE_APP_API_VERSION;

const ACCENT = "var(--tenant-primary-color, #6DBE8B)";

// Pill-shaped input styling shared by all fields (per mobile mockup)
const pillFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "9999px",
    backgroundColor: "#fff",
    boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
    "& fieldset": { borderColor: "transparent" },
    "&:hover fieldset": { borderColor: "rgba(0,0,0,0.12)" },
    "&.Mui-focused fieldset": { borderColor: ACCENT },
  },
  "& .MuiOutlinedInput-input": { padding: "14px 8px 14px 0" },
};

export default function DepartmentSignup() {
  const { qrToken } = useParams();
  const [searchParams] = useSearchParams();
  const navigateTo = useNavigate();

  const [resolving, setResolving] = useState(true);
  const [invalidLink, setInvalidLink] = useState(false);
  const [deptInfo, setDeptInfo] = useState(null); // { department, location, organization }
  const [showQr, setShowQr] = useState(false);

  const [name, setName] = useState("");
  const [email, setEmail] = useState(searchParams.get("email") || "");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const resolve = async () => {
      try {
        const { data } = await axios.get(
          `${API_BASE}/departments/public/by-token/${qrToken}`
        );
        setDeptInfo(data?.data || null);
        if (!data?.data) setInvalidLink(true);
      } catch {
        setInvalidLink(true);
      } finally {
        setResolving(false);
      }
    };
    resolve();
  }, [qrToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) return toast.error("Please enter your name");
    if (!email.trim()) return toast.error("Please enter your email");
    if (!phone.trim()) return toast.error("Please enter your phone number");
    if (!password || password.length < 8)
      return toast.error("Password must be at least 8 characters");
    if (password !== confirmPassword)
      return toast.error("Passwords do not match");

    const [firstName, ...rest] = trimmedName.split(/\s+/);
    const lastName = rest.join(" ") || firstName;

    try {
      setSubmitting(true);
      const { data } = await axios.post(
        `${API_BASE}/user/patient/register`,
        {
          firstName,
          lastName,
          email: email.trim().toLowerCase(),
          phone: phone.trim(),
          password,
          qrToken,
        },
        { withCredentials: true, headers: { "Content-Type": "application/json" } }
      );
      toast.success(data?.message || "Account created. Please verify your email.");
      navigateTo("/verify-otp", { state: { email: email.trim().toLowerCase() } });
    } catch (err) {
      const message = err?.response?.data?.message || "Registration failed";
      // Existing-but-unverified accounts get a fresh OTP — send them to verification
      if (message.toLowerCase().includes("otp") || message.toLowerCase().includes("verify your email")) {
        navigateTo("/verify-otp", { state: { email: email.trim().toLowerCase() } });
        return;
      }
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (resolving) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <CircularProgress sx={{ color: ACCENT }} />
      </div>
    );
  }

  if (invalidLink) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 px-6 text-center">
        <h1 className="text-2xl font-bold text-gray-800 mb-2">Invalid Signup Link</h1>
        <p className="text-gray-500 mb-6">
          This QR code or invitation link is no longer valid. Please contact your
          clinic for a new one, or register normally.
        </p>
        <Link
          to="/register"
          className="px-6 py-3 rounded-full text-white font-medium shadow-md"
          style={{ backgroundColor: "#6DBE8B", background: ACCENT }}
        >
          Go to Registration
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 px-4 py-6 flex flex-col">
      <div className="w-full max-w-md mx-auto flex-1 flex flex-col">
        {/* Back button */}
        <button
          type="button"
          onClick={() => navigateTo(-1)}
          aria-label="Go back"
          className="w-10 h-10 rounded-full bg-white shadow-md flex items-center justify-center mb-6"
        >
          <ArrowBackIosNewIcon sx={{ fontSize: 16, color: "#374151" }} />
        </button>

        {/* Heading with department context */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Create Account</h1>
          <div className="mt-3 inline-flex items-center px-4 py-1.5 rounded-full bg-white shadow-sm text-sm font-semibold text-gray-800">
            {deptInfo?.department?.name}
            {deptInfo?.location?.name ? ` — ${deptInfo.location.name}` : ""}
          </div>
          <p className="mt-3 text-sm text-gray-500">
            {deptInfo?.organization?.name
              ? `Register as a patient of ${deptInfo.organization.name} to get started.`
              : "Create a new account to get started and enjoy seamless access to our features."}
          </p>
        </div>

        {/* QR Code toggle */}
        <div className="flex justify-center mb-2">
          <button
            type="button"
            onClick={() => setShowQr((v) => !v)}
            className="flex items-center gap-1.5 text-sm font-medium px-4 py-2 rounded-full bg-white shadow-sm border border-gray-100 text-gray-600 hover:bg-gray-50 transition"
          >
            <QrCodeIcon sx={{ fontSize: 18 }} />
            {showQr ? "Hide QR Code" : "Show QR Code"}
          </button>
        </div>
        {showQr && (
          <div className="flex flex-col items-center bg-white rounded-2xl shadow-sm p-5 mb-4">
            <QRCodeSVG
              value={window.location.href}
              size={200}
              level="H"
              includeMargin
              style={{ borderRadius: 8 }}
            />
            <p className="text-xs text-gray-400 mt-3 text-center">
              Scan with your phone camera to open on your device
            </p>
          </div>
        )}

        {/* Signup form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <TextField
            fullWidth
            placeholder="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            sx={pillFieldSx}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonOutlineIcon sx={{ color: "#9CA3AF" }} />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            type="email"
            placeholder="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            sx={pillFieldSx}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MailOutlineIcon sx={{ color: "#9CA3AF" }} />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            type="tel"
            placeholder="Phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            sx={pillFieldSx}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PhoneIphoneIcon sx={{ color: "#9CA3AF" }} />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            type={showPassword ? "text" : "password"}
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            sx={pillFieldSx}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlinedIcon sx={{ color: "#9CA3AF" }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword((s) => !s)} edge="end" size="small">
                    {showPassword ? (
                      <VisibilityOutlinedIcon sx={{ color: "#9CA3AF" }} />
                    ) : (
                      <VisibilityOffOutlinedIcon sx={{ color: "#9CA3AF" }} />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            type={showConfirm ? "text" : "password"}
            placeholder="Confirm Password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            sx={pillFieldSx}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlinedIcon sx={{ color: "#9CA3AF" }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowConfirm((s) => !s)} edge="end" size="small">
                    {showConfirm ? (
                      <VisibilityOutlinedIcon sx={{ color: "#9CA3AF" }} />
                    ) : (
                      <VisibilityOffOutlinedIcon sx={{ color: "#9CA3AF" }} />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <button
            type="submit"
            disabled={submitting}
            className="w-full py-3.5 rounded-full text-white font-semibold text-base shadow-md transition active:scale-[0.99] disabled:opacity-60 mt-2"
            style={{ backgroundColor: "#6DBE8B", background: ACCENT }}
          >
            {submitting ? "Creating Account…" : "Create Account"}
          </button>
        </form>

        <p className="text-center text-sm text-gray-600 mt-6">
          Already have an account?{" "}
          <Link to="/login" className="font-semibold" style={{ color: "#6DBE8B" }}>
            Sign In here
          </Link>
        </p>
      </div>
    </div>
  );
}
